import AddIcon from "@mui/icons-material/Add";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getPublicConfig } from "../../api/config/getPublicConfig";
import { getDossier } from "../../api/dossier/getDossier";
import { getDossierStatus } from "../../api/dossier/getDossierStatus";
import { getFinanceApplication } from "../../api/dossier/getFinanceApplication";
import { getListQuotes } from "../../api/dossier/getListQuotes";
import { setPrimaryQuote } from "../../api/dossier/setPrimaryQuote";
import { getProductTours } from "../../api/productTour/getProductTours";
import { deleteQuote } from "../../api/quote/deleteQuote";
import { setQuoteLocked } from "../../api/quote/setQuoteLocked";
import { setQuoteVisibility } from "../../api/quote/setQuoteVisibility";
import AutoPilotAlert from "../../components/Alert";
import APAccordion from "../../components/APAccordion";
import APModal from "../../components/APModal";
import APProductTour from "../../components/APProductTour";
import APSelectDropDown, {
  SelectDropdownOption,
} from "../../components/APSelectDropDown";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import { APIconName } from "../../components/buttons/APIcon";
import APIconButton from "../../components/buttons/APIconButton";
import APMenuButton from "../../components/buttons/APMenuButton";
import BackButton from "../../components/buttons/BackButton";
import CatchEButton from "../../components/buttons/CatchEButton";
import RefreshButton from "../../components/buttons/RefreshButton";
import SyncQuoteButton from "../../components/buttons/SyncQuoteButton";
import PageContainer from "../../components/PageContainer";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import TermsButton from "../../components/TermsButton";
import { removeDuplicates } from "../../helpers/array";
import {
  isAIVehicleReviewEnabled,
  isAPQuoteHideInvalidCalculationsEnabled,
} from "../../helpers/configuration";
import { isDossierActivated } from "../../helpers/dossiers";
import { buildQuoteFilename, downloadFile } from "../../helpers/download";
import {
  filterQuotes,
  QuoteSortOption,
  sortQuotes,
} from "../../helpers/quotes";
import { useAIConsent } from "../../hooks/useAIConsent";
import { useFaqs } from "../../hooks/useFaqs";
import { useApi } from "../../providers/ApiProvider";
import { useAuth } from "../../providers/AuthProvider";
import { useConfirm } from "../../providers/ConfirmProvider";
import { DEFAULT_ACCOUNT } from "../../types/Contact";
import {
  buildNextActionRoute,
  DossierStatusLabelMap,
  NextDriverAction,
} from "../../types/Dossier";
import DossierStatusStepper from "../components/DossierStatusStepper";
import QuoteList from "../components/QuoteList";

const quoteSortOptions: SelectDropdownOption<QuoteSortOption>[] = [
  { id: "taxSavings", label: "Tax Benefit %" },
  { id: "netWeeklyCost", label: "Net Weekly Cost" },
  { id: "lastUpdated", label: "Last Updated" },
];

const ViewDossier = () => {
  const { dossierId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [navigationCheckComplete, setNavigationCheck] = useState(false);
  const [showNextActionModal, setShowNextActionModal] = useState(false);
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [quoteSortOptionId, setQuoteSortOptionId] =
    useState<QuoteSortOption>("lastUpdated");
  const { fetchWithAuth } = useApi();
  const { getScopedPageUrl, getScopedPageConfig, inAppScope } = useAuth();
  const { shouldPrompt: shouldPromptForAIConsent } = useAIConsent();
  const productTourContainerRef = useRef<HTMLDivElement>(null);
  const confirm = useConfirm();
  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const { mutateAsync, isLoading: isLoadingUpdate } = setPrimaryQuote(
    dossierId,
    {
      mutationOptions: {
        successAlertMessage: "Your primary quote has been updated",
      },
    }
  );

  const { mutateAsync: mutateQuoteVisibilityAsync } = setQuoteVisibility({
    mutationOptions: {
      successAlertMessage: "Quote visibility has been updated",
    },
  });

  const { mutateAsync: mutateQuoteLockedAsync } = setQuoteLocked({
    mutationOptions: {
      successAlertMessage: "Quote locked has been updated",
    },
  });

  const { mutateAsync: mutateDeleteQuoteAsync, isLoading: isDeletingQuote } =
    deleteQuote(dossierId, {
      mutationOptions: {
        successAlertMessage: "Quote has been deleted",
      },
    });

  const dossier = getDossier(dossierId);
  const dossierStatus = getDossierStatus(dossierId);

  const quotes = getListQuotes(dossierId);

  const financeApplication = getFinanceApplication(dossierId, {
    appTypeScope: "external",
  });

  const pageId = "dossier-view";
  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId,
    contextIds: {
      opportunityId: dossier.data?.opportunity?.id,
    },
  });

  const productTours = getProductTours(pageId, {
    disabled: inAppScope("internal"),
  });
  const configMap = getPublicConfig();

  if (dossier.isError) {
    throw dossier.error;
  }

  if (dossierStatus.isError) {
    throw dossierStatus.error;
  }

  if (quotes.isError) {
    throw quotes.error;
  }

  if (financeApplication.isError) {
    throw financeApplication.error;
  }

  if (configMap.isError) {
    throw configMap.error;
  }

  const isLoading =
    dossier.isFetching ||
    dossierStatus.isFetching ||
    quotes.isFetching ||
    financeApplication.isFetching ||
    isLoadingFaqs ||
    configMap.isFetching ||
    isDeletingQuote;
  const isSuccess =
    dossier.isSuccess &&
    dossierStatus.isSuccess &&
    quotes.isSuccess &&
    isSuccessFaqs &&
    (financeApplication.isSuccess || financeApplication.isIdle) &&
    configMap.isSuccess;
  const primaryQuoteId = dossier.data?.opportunity?.primaryQuoteId ?? null;
  const primaryQuote = quotes.isSuccess
    ? quotes.data.find(
        (quote) => quote.identifiers.crmQuoteId === primaryQuoteId
      )
    : null;

  useEffect(() => {
    // If there are no quotes then navigate to create quote page
    if (isSuccess) {
      if (dossier.data.quoteCount === 0) {
        navigate(
          `${getScopedPageUrl(
            "createQuote",
            dossierId
          )}?limitedFields=true${location.search.replace("?", "&")}`,
          {
            replace: true,
          }
        );
      } else {
        setNavigationCheck(true);
      }
    }
  }, [isSuccess]);

  const onDownload = async (quoteId: string) => {
    if (quotes.isSuccess && dossier.isSuccess) {
      const quote = quotes.data.find(
        (quote) => quote.identifiers.crmQuoteId === quoteId
      );
      const filename = quote
        ? buildQuoteFilename(
            dossier.data.identity,
            quote.vehicle.make,
            quote.vehicle.model,
            `${quote.term.termMonths}`
          )
        : `${dossier.data.identity}.pdf`;
      const pdf = await fetchWithAuth<Blob>("getQuotePDF", quoteId, {
        responseOptions: { responseType: "blob" },
      });

      downloadFile(pdf, filename);
    }
  };

  const onClone = async (quoteId: string) => {
    navigate(getScopedPageUrl("cloneQuote", quoteId));
  };

  const onEdit = async (quoteId: string) => {
    navigate(getScopedPageUrl("editQuote", quoteId));
  };

  const onAIReview = async (quoteId: string) => {
    const quote = quotes.data?.find(
      (quote) => quote.identifiers.crmQuoteId === quoteId
    );
    if (quote) {
      navigate(
        `${getScopedPageUrl(
          "dossierVehicleReview",
          dossierId,
          quote.vehicle.variantCode
        )}?hideQuoteButton=true`
      );
    }
  };

  const onSetPrimary = async (quoteId: string) => {
    mutateAsync({ quoteId });
  };

  const onSetVisibility = async (quoteId: string, isVisible: boolean) => {
    mutateQuoteVisibilityAsync({ dossierId, quoteId, visibility: isVisible });
  };

  const onSetLocked = async (quoteId: string, isLocked: boolean) => {
    mutateQuoteLockedAsync({ dossierId, quoteId, locked: isLocked });
  };

  const onNext = async (nextAction: NextDriverAction) => {
    const route = buildNextActionRoute(dossierId, nextAction);
    navigate(route);
  };

  const onUser = async () => {
    if (dossier.data) {
      navigate(getScopedPageUrl("viewKeycloakUser", dossier.data.crmContactId));
    }
  };

  const onNew = async () => {
    if (quotes.isSuccess && dossier.isSuccess) {
      const quoteId = primaryQuoteId || quotes.data[0].identifiers.crmQuoteId;

      navigate(getScopedPageUrl("cloneQuote", quoteId));
    }
  };

  const onDeleteQuote = async (quoteId: string) => {
    const ok = await confirm({
      title: "Delete quote?",
      description: "Are you sure you want to delete this quote?",
    });

    if (ok) {
      mutateDeleteQuoteAsync(quoteId);
    }
  };

  const onFinanceApplication = async () => {
    navigate(getScopedPageUrl("dossierFinanceApplication", dossierId));
  };

  // TODO: add once we release activated dossiers
  const onActivatedDossier = async () => {
    navigate(getScopedPageUrl("dossierActivated", dossierId));
  };

  const onStatusItemClick = () => {
    setShowNextActionModal(true);
  };

  const onCloseNextActionModal = () => {
    setShowNextActionModal(false);
  };

  const onToggleSortOptions = () => {
    setShowSortOptions(!showSortOptions);
  };

  const onQuoteSortChange = (optionId: QuoteSortOption) => {
    setQuoteSortOptionId(optionId);
  };

  const isActivatedDossier = dossier.data && isDossierActivated(dossier.data);
  const hideInvalidCalculationsFlag =
    configMap.isSuccess &&
    isAPQuoteHideInvalidCalculationsEnabled(configMap.data);
  const hideInvalidCalculations =
    hideInvalidCalculationsFlag && inAppScope("external");
  const showInvalidCalculationToggle =
    hideInvalidCalculationsFlag && inAppScope("internal");
  const hasLockedQuote =
    quotes.isSuccess && quotes.data.some((quote) => quote.config.isLocked);
  const canCreateQuote =
    dossier.isSuccess && !!dossier.data.opportunity?.canCreateQuote;
  const filteredQuotes = quotes.isSuccess
    ? filterQuotes(quotes.data, inAppScope("internal"))
    : [];
  const comparableQuotes = quotes.isSuccess
    ? filterQuotes(quotes.data, inAppScope("internal"), hideInvalidCalculations)
    : [];
  const visibleQuotes = quotes.isSuccess
    ? sortQuotes(
        filterQuotes(quotes.data, false),
        quoteSortOptionId,
        primaryQuoteId
      )
    : [];

  const filteredVariantCodes = removeDuplicates(
    visibleQuotes.map((quote) => quote.vehicle.variantCode)
  );
  const vehicleCompareVisible = filteredVariantCodes.length > 1;
  const quoteCompareVisible = comparableQuotes.length > 1;
  const financeCompareVisible =
    !hideInvalidCalculations ||
    primaryQuote?.config.calculationStatus === "valid";
  const compareVisible =
    vehicleCompareVisible || quoteCompareVisible || financeCompareVisible;

  const isVehicleReviewEnabled =
    configMap.isSuccess && isAIVehicleReviewEnabled(configMap.data);

  // Render Functions
  const renderAppLocation = faqProps.renderAppLocation;

  const sortDropdown = (
    <Box sx={{ my: 1, display: "flex", justifyContent: "end" }}>
      <Box sx={{ width: "168px", display: "flex", justifyContent: "end" }}>
        <APSelectDropDown
          label="Sort By"
          selectedOptionId={quoteSortOptionId}
          options={quoteSortOptions}
          onOptionChange={onQuoteSortChange}
        />
      </Box>
    </Box>
  );

  return (
    <PageContainer loading={isLoading || !navigationCheckComplete}>
      <>
        {isSuccess && navigationCheckComplete && (
          <Stack spacing={2} ref={productTourContainerRef}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  columnGap: 1,
                }}
              >
                <Box>
                  <Typography variant="titleSmall" color="onSurface.main">
                    {dossier.data.identity} - {dossier.data.crmContactName}
                  </Typography>
                  {dossier.data.opportunity &&
                    dossier.data.opportunity.parentAccountId !==
                      DEFAULT_ACCOUNT && (
                      <Typography variant="titleSmall" color="onSurface.main">
                        {dossier.data.opportunity.parentAccountName}
                      </Typography>
                    )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    columnGap: 1,
                    alignItems: "center",
                    flexDirection: {
                      xs: inAppScope("internal") ? "column-reverse" : "row",
                      sm: "row",
                    },
                    rowGap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      columnGap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <RestrictedAppScope scope="internal">
                        <SyncQuoteButton
                          contactId={dossier.data.crmContactId}
                        />
                      </RestrictedAppScope>
                    </Box>
                    {renderAppLocation(
                      "QuoteList-SortOrder",
                      <Box>
                        <APIconButton
                          iconName="sort"
                          onClick={onToggleSortOptions}
                          sx={{
                            display: {
                              xs: "initial",
                              sm: "none",
                            },
                          }}
                        />
                        <Box
                          sx={{
                            display: { xs: "none", sm: "initial" },
                          }}
                        >
                          {sortDropdown}
                        </Box>
                      </Box>
                    )}
                    <RefreshButton />
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  display: {
                    xs: "initial",
                    sm: "none",
                  },
                }}
              >
                <Collapse in={showSortOptions}>{sortDropdown}</Collapse>
              </Box>
            </Box>

            {canCreateQuote && dossier.data.status !== "Active" && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  columnGap: 2,
                }}
              >
                <Box>
                  {filteredQuotes.length <
                  getScopedPageConfig<number>("dossierMaxQuoteCount") ? (
                    <Button
                      size="large"
                      color="primary"
                      variant="contained"
                      disableElevation={true}
                      startIcon={<AddIcon />}
                      onClick={() => onNew()}
                    >
                      New
                    </Button>
                  ) : (
                    <Typography variant="titleMedium">
                      *Max quotes reached, contact Auto-UX if you require more.
                    </Typography>
                  )}
                </Box>
                {compareVisible && (
                  <Box>
                    <APMenuButton
                      text="Compare"
                      items={[
                        ...(vehicleCompareVisible
                          ? [
                              {
                                id: "vehicles",
                                text: "Vehicles",
                                icon: "car" as APIconName,
                                to: `${getScopedPageUrl(
                                  "dossierVehicleComparison",
                                  dossierId
                                )}?codes=${filteredVariantCodes.join(
                                  ","
                                )}&hideQuoteButton=true`,
                              },
                            ]
                          : []),
                        ...(quoteCompareVisible
                          ? [
                              {
                                id: "quotes",
                                text: "Quotes",
                                icon: "quote" as APIconName,
                                to: getScopedPageUrl(
                                  "compareQuotes",
                                  dossierId
                                ),
                              },
                            ]
                          : []),
                        ...(financeCompareVisible
                          ? [
                              {
                                id: "finance",
                                text: "Finance",
                                icon: "money" as APIconName,
                                to: getScopedPageUrl(
                                  "viewQuoteFinanceComparison",
                                  primaryQuoteId ?? ""
                                ),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </Box>
                )}
              </Box>
            )}

            <QuoteList
              quotes={sortQuotes(
                filteredQuotes,
                quoteSortOptionId,
                primaryQuoteId
              )}
              primaryQuoteId={primaryQuoteId}
              actions={{
                actionHandlers: {
                  onSetPrimary: onSetPrimary,
                  onDownload: onDownload,
                  onClone: onClone,
                  onEdit: onEdit,
                  onSetVisibility: onSetVisibility,
                  onSetLocked: onSetLocked,
                  onNext: () =>
                    dossierStatus.isSuccess &&
                    onNext(dossierStatus.data.nextDriverAction),
                  onAIReview: onAIReview,
                  onDelete: onDeleteQuote,
                },
                bottomActions: [
                  "onClone",
                  "onSetPrimary",
                  "onDownload",
                  "onSetVisibility",
                  "onSetLocked",
                  "onEdit",
                  "onNext",
                  "onDelete",
                ],
              }}
              config={{
                disablePrimaryToggle: isLoadingUpdate || hasLockedQuote,
                hideClone:
                  (quotes.isSuccess &&
                    filteredQuotes.length >=
                      getScopedPageConfig<number>("dossierMaxQuoteCount")) ||
                  !canCreateQuote,
                hideAIReview: !isVehicleReviewEnabled,
                shouldPromptForAIConsent: shouldPromptForAIConsent,
                hideInvalidCalculations: hideInvalidCalculations,
                showInvalidCalculationToggle: showInvalidCalculationToggle,
              }}
            />

            <TermsButton termsId="quote" />

            {dossier.data.status === "New" && (
              <Box>
                <APAccordion title="What is Next?" defaultExpanded>
                  <Box sx={{ mt: 1, ml: 1 }}>
                    <DossierStatusStepper
                      dossierStatus={dossierStatus.data}
                      onItemClick={
                        dossier.data.noteToDriver
                          ? onStatusItemClick
                          : undefined
                      }
                      appLocations={{
                        renderAppLocation,
                        firstTodoIdentifier: "QuoteList-StatusCurrentStage",
                      }}
                    ></DossierStatusStepper>
                  </Box>
                </APAccordion>
                <APModal
                  isOpen={showNextActionModal}
                  onClose={onCloseNextActionModal}
                  confirmButton={
                    <Button
                      size="large"
                      color="secondary"
                      variant="contained"
                      disableElevation={true}
                      startIcon={<PlayArrowOutlinedIcon />}
                      onClick={() =>
                        onNext(dossierStatus.data.nextDriverAction)
                      }
                    >
                      Next
                    </Button>
                  }
                >
                  {dossier.data.noteToDriver && (
                    <Box>
                      <AutoPilotAlert
                        severity="info"
                        message={dossier.data.noteToDriver}
                        asHTML={true}
                      />
                    </Box>
                  )}
                </APModal>
              </Box>
            )}
            {dossier.data.status === "New" && dossier.data.noteToDriver && (
              <Box>
                <AutoPilotAlert
                  severity="info"
                  message={dossier.data.noteToDriver}
                  asHTML={true}
                />
              </Box>
            )}

            <Box
              sx={{
                pt: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Stack
                spacing={1}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{ display: "flex", columnGap: 1, alignItems: "center" }}
                >
                  <BackButton />
                  <RestrictedAppScope scope="internal">
                    {dossier.data.opportunity?.catchEContactUrl && (
                      <CatchEButton
                        url={dossier.data.opportunity.catchEContactUrl}
                        sx={{ p: { sm: 1.5, xs: 1 } }}
                      />
                    )}
                  </RestrictedAppScope>
                </Box>
                <Box
                  sx={{ display: "flex", columnGap: 1, alignItems: "center" }}
                >
                  <RestrictedAppScope scope="external">
                    {financeApplication.data &&
                      !financeApplication.data.isHidden && (
                        <Button
                          size="large"
                          color="primary"
                          variant="contained"
                          disableElevation={true}
                          onClick={() => onFinanceApplication()}
                        >
                          Finance App
                        </Button>
                      )}
                  </RestrictedAppScope>

                  <RestrictedAppScope scope="internal">
                    <Button
                      size="large"
                      color="primary"
                      variant="contained"
                      disableElevation={true}
                      onClick={onUser}
                    >
                      User
                    </Button>
                  </RestrictedAppScope>
                </Box>
                <RestrictedAppScope scope="external">
                  <APContactHelpButton
                    regardingOpportunityId={
                      dossier.data.opportunity?.id ?? undefined
                    }
                  />
                </RestrictedAppScope>
              </Stack>
              <Stack
                spacing={2}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <RestrictedAppScope scope="external">
                  <Button
                    size="large"
                    color="secondary"
                    variant="contained"
                    disableElevation={true}
                    startIcon={<PlayArrowOutlinedIcon />}
                    onClick={() => onNext(dossierStatus.data.nextDriverAction)}
                  >
                    Next
                  </Button>
                </RestrictedAppScope>

                {isActivatedDossier && (
                  <Button
                    size="large"
                    color="primary"
                    variant="contained"
                    disableElevation={true}
                    onClick={() => onActivatedDossier()}
                  >
                    {DossierStatusLabelMap[dossier.data.status]} Car
                  </Button>
                )}
              </Stack>
            </Box>
          </Stack>
        )}
        {productTours.isSuccess && productTours.data[0] && (
          <APProductTour
            key={productTours.data[0].id}
            tour={productTours.data[0]}
            tourContainerRef={productTourContainerRef}
          />
        )}
      </>
    </PageContainer>
  );
};

export default ViewDossier;
